import { useEffect } from "react";

import { useKpiDatasetKind } from "./useKpiDatasetKind";
import type { XFactorKpiCategoryDetail } from "../Pages/TempViewsRendering/lib/deriveKpi";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchKpiAccountBriefing,
  kpiAccountBriefingKey,
} from "../Store/Reducers/XFactorKpiSlice";

export type XFactorKpiAccountBriefingState = {
  data: XFactorKpiCategoryDetail;
  date: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Resolves an EB account to its newest live KPI Metrics Briefing "overall"
 * category detail — the same 8-block payload the KPI Situation Card reads.
 *
 * `measurementDate` is an optional exact-date override. Omit it to let
 * xfactor resolve the newest *briefed* date itself server-side (live as of
 * 2026-09-21 — see docs/xfactor/kpi-briefings.md). Pass useXFactorKpiScoresLatest
 * first if you need to know whether a briefing exists before calling this.
 *
 * Reads the org Synthetic-data lane via `dataset_kind` so synthetic briefings
 * surface when the toggle is ON. Data lives in XFactorKpiSlice.
 */
export const useXFactorKpiAccountBriefing = (
  accountId?: string,
  measurementDate?: string
): XFactorKpiAccountBriefingState => {
  const dispatch = useAppDispatch();
  const datasetKind = useKpiDatasetKind();
  const key = accountId
    ? kpiAccountBriefingKey({ accountId, datasetKind, measurementDate })
    : null;
  const entry = useAppSelector(state =>
    key ? state.xfactorKpi.accountBriefings[key] : undefined
  );

  useEffect(() => {
    if (!accountId) return;
    dispatch(
      fetchKpiAccountBriefing({ accountId, datasetKind, measurementDate })
    );
  }, [dispatch, accountId, measurementDate, datasetKind]);

  return {
    data: entry?.data ?? null,
    date: entry?.date ?? null,
    loading: entry ? entry.loading : Boolean(accountId),
    error: entry?.error ?? null,
  };
};

export default useXFactorKpiAccountBriefing;
